// The full SKU of a sellable unit is the product SKU concatenated with the
// option SKU, which carries its own leading hyphen — `JB1018100` +
// `-CF484-W03`. The product SKU is therefore everything before the first
// hyphen, and it is told apart from an option code by one property: it
// contains a run of at least five consecutive digits. Every product SKU on
// dev does (`JB1018100`, `JB1371800L`, `JB10260A0`, `SS1125310`, `FS1012300`;
// 646 products / 36,647 variants, 2026-09-18) and no option code comes close
// — colour and size codes carry runs of at most three (`CF441`, `WS03`,
// `18X18`, `RUG7'10x10'3`, `ART8.5x11`), so a bare option SKU is never
// mistaken for a full one.
//
// Nothing about the family code — its length, its letters, its case — is part
// of the rule on purpose. The catalogue already holds `JB`, `SS` and `FS`, and
// a family the rule did not anticipate would silently fail the SKU join:
// every card of that product would fall back or drop (PR 7222 review). A
// digit *count* would not do either: a rug size carries six digits in total.
const PRODUCT_SKU_DIGIT_RUN = /\d{5}/;

const isProductSku = segment => PRODUCT_SKU_DIGIT_RUN.test(segment);

/**
 * Reduces any SKU form to the bare option SKU, so the hit's `product_variants`
 * and the enriched variants can be joined on one key.
 *
 * The two sides do not agree on format: enrichment (`getProductsStaticData`)
 * returns the full SKU (`JB1018100-CF484-W03`, verified on dev), while the
 * index carries the option SKU Caravel keys its feeds on — hyphen-prefixed
 * (`-CF484-W03`) on most samples, bare (`CF484-W03`) on some. All three forms
 * normalize to `CF484-W03`, and re-normalizing is a no-op, so either side may
 * be fed in.
 *
 * A bare product SKU (no option part, so no hyphen) is returned unchanged —
 * it is not an option SKU and must not match one.
 */
const normalizeOptionSku = sku => {
  if (sku == null) {
    return '';
  }

  const value = String(sku);
  const firstHyphen = value.indexOf('-');
  const withoutProductSku =
    firstHyphen > 0 && isProductSku(value.slice(0, firstHyphen))
      ? value.slice(firstHyphen + 1)
      : value;

  return withoutProductSku.replace(/^-+/, '');
};

export default normalizeOptionSku;
